import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Trash2, Settings, ExternalLink, Eye } from 'lucide-react';
import { getPlatformById } from '@/lib/social-platforms';
import { getPlatformDisplayName } from '@/lib/utils';
import { TwitterEmbed } from '@/components/social-widgets/TwitterEmbed';
import { FacebookEmbed } from '@/components/social-widgets/FacebookEmbed';
import { RedditEmbed } from '@/components/social-widgets/RedditEmbed';

interface ConnectedAccount {
  id: string;
  platform: string;
  account_username: string;
  is_active: boolean;
  created_at: string;
}

interface ConnectedPlatformsListProps {
  accounts: ConnectedAccount[];
  onDisconnect?: (accountId: string, platform: string) => void;
  onConfigure?: (accountId: string, platform: string) => void;
  onView?: (accountId: string, platform: string) => void;
  className?: string;
}

export const ConnectedPlatformsList = ({ 
  accounts, 
  onDisconnect, 
  onConfigure,
  onView,
  className = ""
}: ConnectedPlatformsListProps) => {
  // Filter to only show truly connected accounts (consistent with other components)
  const connectedAccounts = React.useMemo(() => {
    return accounts.filter(account => {
      return account.is_active && 
             account.account_username &&
             account.account_username.trim() !== '' &&
             account.account_username !== 'placeholder' &&
             !account.account_username.startsWith('temp_');
    });
  }, [accounts]);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<ConnectedAccount | null>(null);

  const handleView = (account: ConnectedAccount) => {
    setSelectedAccount(account);
    setViewDialogOpen(true);
    onView?.(account.id, account.platform);
  };

  const renderPlatformWidget = (account: ConnectedAccount) => {
    switch (account.platform) {
      case 'twitter':
        return (
          <TwitterEmbed 
            username={account.account_username}
            accountId={account.id}
          />
        );
      case 'facebook':
        return (
          <FacebookEmbed 
            pageId={account.account_username}
            pageName={account.account_username}
          />
        );
      case 'reddit':
        return (
          <RedditEmbed 
            username={account.account_username}
          />
        );
      default:
        return (
          <Card className="h-[500px]">
            <CardContent className="flex items-center justify-center h-full">
              <p className="text-muted-foreground">Widget not available for this platform</p>
            </CardContent>
          </Card>
        );
    }
  };
  if (!connectedAccounts || connectedAccounts.length === 0) {
    return (
      <Card className={className}>
        <CardContent className="pt-6 text-center">
          <p className="text-muted-foreground">No connected platforms yet.</p>
          <p className="text-sm text-muted-foreground mt-1">
            Connect your social media accounts to get started.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className={`space-y-4 ${className}`}>
      {connectedAccounts.map((account) => {
        const platformData = getPlatformById(account.platform);
        const Icon = platformData?.icon;

        return (
          <Card key={account.id}>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {Icon && (
                    <div 
                      className="p-2 rounded-lg"
                      style={{ backgroundColor: `${platformData.color}20` }}
                    >
                      <Icon 
                        className="h-5 w-5" 
                        style={{ color: platformData.color }}
                      />
                    </div>
                  )}
                  <div>
                    <CardTitle className="text-lg">
                      {getPlatformDisplayName(account.platform as any)}
                    </CardTitle>
                    <p className="text-sm text-muted-foreground">
                      @{account.account_username}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <Badge 
                    variant={account.is_active ? "secondary" : "outline"}
                    className={account.is_active ? "text-green-600 bg-green-50" : ""}
                  >
                    {account.is_active ? "Active" : "Inactive"}
                  </Badge>
                </div>
              </div>
            </CardHeader>
            
            <CardContent className="pt-0">
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">
                  Connected on {new Date(account.created_at).toLocaleDateString()}
                </p>
                
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleView(account)}
                  >
                    <Eye className="h-4 w-4 mr-1" />
                    View
                  </Button>
                  
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onConfigure?.(account.id, account.platform)}
                  >
                    <Settings className="h-4 w-4 mr-1" />
                    Configure
                  </Button>
                  
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onDisconnect?.(account.id, account.platform)}
                    className="text-destructive hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4 mr-1" />
                    Disconnect
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
      
      {/* View Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-4xl h-[600px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedAccount && (
                <>
                  {(() => {
                    const platformData = getPlatformById(selectedAccount.platform);
                    const Icon = platformData?.icon;
                    return Icon && (
                      <div 
                        className="p-2 rounded-lg"
                        style={{ backgroundColor: `${platformData.color}20` }}
                      >
                        <Icon 
                          className="h-5 w-5" 
                          style={{ color: platformData.color }}
                        />
                      </div>
                    );
                  })()}
                  {getPlatformDisplayName(selectedAccount.platform as any)} Feed - @{selectedAccount.account_username}
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-hidden">
            {selectedAccount && renderPlatformWidget(selectedAccount)}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};